import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Responsive } from '@cian/ui-kit';
import { useEventCallback } from '@cian/react-utils';
import { call } from '../../actions/call';
import { BuilderCardMobile, BuilderCardDesktop, IBuilderCardBaseProps } from '../../components/BuilderCard';
import { EProjectsType, IProjectImage } from '../../types/builders';
import { formatMaterials, formatProjectsPrice, formatProjectsTypes, getPageLink } from '../../utils/builders';
import { PhoneButtonContainer } from '../PhoneButtonContainer';
import { trackClickCall, trackClickLink, trackClickPhone } from './tracking';
import { GalleryContainer } from '../GalleryContainer';
import { Dispatch } from '../../types/redux';
import { LabelContainer } from '../LabelContainer';

export interface IBuilderCardContainerProps {
  index: number;
  age: number;
  name: string;
  logo: string;
  phone: string;
  companyId: number;
  images: IProjectImage[];
  materials: string[];
  otherMaterials?: string;
  projectStartPrice: number;
  projectsTypes: EProjectsType[];
}

export const BuilderCardContainer: React.FC<IBuilderCardContainerProps> = ({
  index,
  age,
  name,
  logo,
  phone,
  companyId,
  images,
  materials,
  otherMaterials,
  projectStartPrice,
  projectsTypes,
}) => {
  const position = index + 1;
  const link = React.useMemo(() => getPageLink(companyId), [companyId]);
  const formattedMaterials = React.useMemo(
    () => formatMaterials(materials, otherMaterials),
    [materials, otherMaterials],
  );
  const formattedProjectsTypes = React.useMemo(() => formatProjectsTypes(projectsTypes), [projectsTypes]);
  const formattedProjectsPrice = React.useMemo(() => formatProjectsPrice(projectStartPrice), [projectStartPrice]);

  const dispatch: Dispatch = useDispatch();
  const handleClickPhone = useEventCallback(() => {
    trackClickPhone(companyId, name, position);
  });
  const handleClickCall = useEventCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    trackClickCall(companyId, name, position);
    dispatch(call(companyId, name, position));
  });
  const handleClickLink = useEventCallback(() => {
    trackClickLink(companyId, name, position);
  });

  const baseProps: IBuilderCardBaseProps = {
    name,
    logo,
    phone,
    link,
    phoneButton: <PhoneButtonContainer phone={phone} onShow={handleClickPhone} />,
    materials: formattedMaterials,
    projectStartPrice: formattedProjectsPrice,
    projectsTypes: formattedProjectsTypes,
    onClickLink: handleClickLink,
    label: <LabelContainer builderId={companyId} />,
  };

  return (
    <>
      <Responsive desktop>
        <BuilderCardDesktop {...baseProps} age={age} />
      </Responsive>
      <Responsive tablet phone>
        <BuilderCardMobile
          {...baseProps}
          gallery={<GalleryContainer images={images} />}
          onClickCall={handleClickCall}
        />
      </Responsive>
    </>
  );
};
